import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Upload, Input, message, Checkbox } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
} from '@mui/material';
import { Col, Row } from 'antd';
import { useDispatch } from 'react-redux';
import { handleAiGenerateStandards, handleBulkCreateStandards } from 'store/story/thunks';
import { useStandards } from 'store/story/selectors';
import { STANDARD_GRADE_LEVEL } from 'utils/config/reference';
import { isIllinoisOrCommonCoreState, statesMatch, getStateNameFromCode, getStateCodeFromName } from 'utils/lib/standardHelper';
import Loading from 'components/Loading';
import styled from 'styled-components';

const { TextArea } = Input;

const ModalContentWrapper = styled.div`
  padding: 20px;
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  margin-bottom: 8px;
  font-weight: 500;
`;

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const StandardItem = styled.div`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StandardInfo = styled.div`
  flex: 1;
`;

const StandardAbbr = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const StandardDesc = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
`;

const ProgressBarContainer = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
`;

const ProgressBarWrapper = styled.div`
  margin-bottom: 16px;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;


const AiGenerateStandardsModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const existingStandards = useStandards();
  const [step, setStep] = useState(1);
  const [pdfFile, setPdfFile] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [state, setState] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedStandards, setGeneratedStandards] = useState([]);
  const [selectedStandards, setSelectedStandards] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [streamingCount, setStreamingCount] = useState(0);
  const [streamingComplete, setStreamingComplete] = useState(false);
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const pdfProgressIntervalRef = useRef(null);
  const generationProgressIntervalRef = useRef(null);
  const pdfUploadCompleteRef = useRef(false);
  const generationStartedRef = useRef(false);
  const firstStandardReceivedRef = useRef(false);

  const handleFileChange = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Please upload a PDF file only');
      return false;
    }
    setPdfFile(file);
    return false;
  };

  const getGradeLevelsForState = (selectedState) => {
    if (isIllinoisOrCommonCoreState(selectedState)) {
      return ["6", "7", "8", "9-10", "11-12"];
    } else {
      return ["6", "7", "8", "9", "10", "11", "12"];
    }
  };

  const handleGenerate = async () => {
    if (!pdfFile) {
      message.error('Please upload a PDF file');
      return;
    }
    if (!state) {
      message.error('Please select a state');
      return;
    }
    if (!gradeLevel) {
      message.error('Please select a grade level');
      return;
    }

    setGenerating(true);
    setGeneratedStandards([]);
    setStreamingCount(0);
    setStreamingComplete(false);
    setPdfUploadProgress(0);
    setGenerationProgress(0);
    setPdfUploaded(false);
    pdfUploadCompleteRef.current = false;
    generationStartedRef.current = false;
    firstStandardReceivedRef.current = false;

    if (pdfProgressIntervalRef.current) {
      clearInterval(pdfProgressIntervalRef.current);
      pdfProgressIntervalRef.current = null;
    }
    if (generationProgressIntervalRef.current) {
      clearInterval(generationProgressIntervalRef.current);
      generationProgressIntervalRef.current = null;
    }

    pdfProgressIntervalRef.current = setInterval(() => {
      setPdfUploadProgress(prev => {
        if (prev >= 90 || pdfUploadCompleteRef.current) {
          if (pdfProgressIntervalRef.current) {
            clearInterval(pdfProgressIntervalRef.current);
            pdfProgressIntervalRef.current = null;
          }
          return pdfUploadCompleteRef.current ? 100 : 90;
        }
        return Math.min(prev + 5, 90);
      });
    }, 2000);
    try {
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const base64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(base64);
        };
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
      });

      const pdfBase64 = await toBase64(pdfFile);

      const existingStandardsArray = Array.isArray(existingStandards) ? existingStandards : [];

      const handleStatusUpdate = (statusMessage) => {
        if (statusMessage === 'PDF uploaded successfully') {
          pdfUploadCompleteRef.current = true;
          setPdfUploadProgress(100);
          setPdfUploaded(true);
          if (pdfProgressIntervalRef.current) {
            clearInterval(pdfProgressIntervalRef.current);
            pdfProgressIntervalRef.current = null;
          }
          if (!generationStartedRef.current) {
            generationStartedRef.current = true;
            if (generationProgressIntervalRef.current) {
              clearInterval(generationProgressIntervalRef.current);
              generationProgressIntervalRef.current = null;
            }
            generationProgressIntervalRef.current = setInterval(() => {
              setGenerationProgress(prev => {
                if (prev >= 95 || firstStandardReceivedRef.current) {
                  if (generationProgressIntervalRef.current) {
                    clearInterval(generationProgressIntervalRef.current);
                    generationProgressIntervalRef.current = null;
                  }
                  return firstStandardReceivedRef.current ? 100 : 95;
                }
                return Math.min(prev + 4, 95);
              });
            }, 1000);
          }
        } else if (statusMessage === 'Start standard generation') {
          if (!generationStartedRef.current) {
            generationStartedRef.current = true;
            if (generationProgressIntervalRef.current) {
              clearInterval(generationProgressIntervalRef.current);
              generationProgressIntervalRef.current = null;
            }
            generationProgressIntervalRef.current = setInterval(() => {
              setGenerationProgress(prev => {
                if (prev >= 95 || firstStandardReceivedRef.current) {
                  if (generationProgressIntervalRef.current) {
                    clearInterval(generationProgressIntervalRef.current);
                    generationProgressIntervalRef.current = null;
                  }
                  return firstStandardReceivedRef.current ? 100 : 95;
                }
                return Math.min(prev + 4, 95);
              });
            }, 1000);
          }
        }
      };

      const handleStreamUpdate = (newStandard, allStandards) => {
        if (!firstStandardReceivedRef.current) {
          firstStandardReceivedRef.current = true;
          setGenerationProgress(100);
          if (generationProgressIntervalRef.current) {
            clearInterval(generationProgressIntervalRef.current);
            generationProgressIntervalRef.current = null;
          }
        }
        
        const stateNameForCompare = getStateNameFromCode(state) || state;
        let standardStateName = newStandard.state ? (getStateNameFromCode(newStandard.state) || newStandard.state) : stateNameForCompare;
        
        // Convert Common Core to COMMONCORE, otherwise uppercase and trim
        if (standardStateName && standardStateName.toUpperCase().trim() === 'COMMON CORE') {
          standardStateName = 'COMMONCORE';
        } else if (standardStateName && standardStateName !== 'COMMONCORE') {
          standardStateName = standardStateName.toUpperCase().trim();
        }
        
        const standardWithStateName = {
          ...newStandard,
          state: standardStateName
        };
        
        const existingStandard = existingStandardsArray.find(
          existing => {
            const existingAbbr = String(existing.abbr || '').toUpperCase().trim();
            const standardAbbr = String(standardWithStateName.abbr || '').toUpperCase().trim();
            const abbrMatch = existingAbbr && standardAbbr && existingAbbr === standardAbbr;
            
            const existingState = existing.state || '';
            const existingStateName = getStateNameFromCode(existingState) || existingState;
            const stateMatch = existingStateName && standardStateName && statesMatch(existingStateName, standardStateName);
            
            const existingGrade = String(existing.grade_level || '').trim();
            const currentGrade = String(gradeLevel || '').trim();
            const gradeMatch = existingGrade && currentGrade && existingGrade === currentGrade;
            
            return abbrMatch && stateMatch && gradeMatch;
          }
        );

        const standardWithExistsCheck = {
          ...standardWithStateName,
          already_exists: !!existingStandard
        };

        setGeneratedStandards(prev => {
          const updated = [...prev, standardWithExistsCheck];
          setStreamingCount(updated.length);
          
          if (prev.length === 0) {
            setStep(2);
            if (!standardWithExistsCheck.already_exists) {
              setSelectedStandards(new Set([0]));
            } else {
              setSelectedStandards(new Set());
            }
          } else if (!standardWithExistsCheck.already_exists) {
            setSelectedStandards(prevSelected => {
              const newSelected = new Set(prevSelected);
              newSelected.add(updated.length - 1);
              return newSelected;
            });
          }
          
          return updated;
        });
      };

      let stateForAI = (state && state.toUpperCase().trim() === 'COMMON CORE') 
        ? 'COMMONCORE' 
        : (getStateNameFromCode(state) || state);
      
      // Uppercase and trim (except Common Core which is already COMMONCORE)
      if (stateForAI !== 'COMMONCORE') {
        stateForAI = stateForAI.toUpperCase().trim();
      }
      
      const enhancedPrompt = prompt 
        ? `${prompt}\n\nIMPORTANT: For each standard, you must also provide a Spanish abbreviation (abbr_spanish) field. The Spanish abbreviation should be the Spanish translation or equivalent of the English abbreviation.`
        : 'IMPORTANT: For each standard, you must also provide a Spanish abbreviation (abbr_spanish) field. The Spanish abbreviation should be the Spanish translation or equivalent of the English abbreviation.';
      
      const result = await dispatch(handleAiGenerateStandards({
        pdf: {
          filename: pdfFile.name,
          isBase64: true,
          content: pdfBase64
        },
        prompt: enhancedPrompt,
        state: stateForAI,
        grade_level: gradeLevel
      }, handleStreamUpdate, handleStatusUpdate));
      
      setStreamingComplete(true);

      if (result && Array.isArray(result) && result.length > 0) {
        const standardsWithExistsCheck = result.map(standard => {
          let standardStateName = standard.state ? (getStateNameFromCode(standard.state) || standard.state) : stateForAI;
          if (standardStateName && standardStateName.toUpperCase().trim() === 'COMMON CORE') {
            standardStateName = 'COMMONCORE';
          } else if (standardStateName && standardStateName !== 'COMMONCORE') {
            standardStateName = standardStateName.toUpperCase().trim();
          }
          
          const standardWithStateName = {
            ...standard,
            state: standardStateName
          };
          
          const existingStandard = existingStandardsArray.find(
            existing => {
              const existingAbbr = String(existing.abbr || '').toUpperCase().trim();
              const standardAbbr = String(standardWithStateName.abbr || '').toUpperCase().trim();
              const abbrMatch = existingAbbr && standardAbbr && existingAbbr === standardAbbr;
              
              const existingState = existing.state || '';
              const existingStateName = getStateNameFromCode(existingState) || existingState;
              const stateMatch = existingStateName && standardStateName && statesMatch(existingStateName, standardStateName);
              
              const existingGrade = String(existing.grade_level || '').trim();
              const currentGrade = String(gradeLevel || '').trim();
              const gradeMatch = existingGrade && currentGrade && existingGrade === currentGrade;
              
              return abbrMatch && stateMatch && gradeMatch;
            }
          );
          return {
            ...standardWithStateName,
            already_exists: !!existingStandard
          };
        });
        
        setGeneratedStandards(standardsWithExistsCheck);
        const initialSelected = new Set(
          standardsWithExistsCheck
            .map((s, index) => !s.already_exists ? index : null)
            .filter(index => index !== null)
        );
        setSelectedStandards(initialSelected);
        setStep(2);
        message.success(`Successfully generated ${result.length} standard(s)`);
      } else if (result && Array.isArray(result) && result.length === 0) {
        if (generatedStandards.length === 0) {
          message.warning('No standards were generated. Please try with a different PDF or prompt.');
        } else {
          setStep(2);
          message.success(`Successfully generated ${generatedStandards.length} standard(s)`);
        }
      } else {
        if (generatedStandards.length > 0) {
          setStep(2);
          message.success(`Successfully generated ${generatedStandards.length} standard(s)`);
        } else {
          message.error('Failed to generate standards. Please try again.');
        }
      }
    } catch (error) {
      const errorMessage = error?.message || 'Failed to generate standards. Please try again.';
      message.error(errorMessage);
      console.error('Generate error:', error);
      if (generatedStandards.length > 0) {
        setStep(2);
        message.info(`Partial results: ${generatedStandards.length} standard(s) generated before error.`);
      }
    } finally {
      setGenerating(false);
      setStreamingCount(0);
      setStreamingComplete(true);
      if (pdfProgressIntervalRef.current) {
        clearInterval(pdfProgressIntervalRef.current);
        pdfProgressIntervalRef.current = null;
      }
      if (generationProgressIntervalRef.current) {
        clearInterval(generationProgressIntervalRef.current);
        generationProgressIntervalRef.current = null;
      }
    }
  };

  const handleCheckboxChange = (index, checked) => {
    const newSelected = new Set(selectedStandards);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedStandards(newSelected);
  };

  const handleCreateSelected = async () => {
    if (selectedStandards.size === 0) {
      message.warning('Please select at least one standard to create');
      return;
    }

    setCreating(true);
    try {
      let stateForCreate = (state && state.toUpperCase().trim() === 'COMMON CORE') 
        ? 'COMMONCORE' 
        : (getStateNameFromCode(state) || state);
      
      // Uppercase and trim (except Common Core which is already COMMONCORE)
      if (stateForCreate !== 'COMMONCORE') {
        stateForCreate = stateForCreate.toUpperCase().trim();
      }
      
      const standardsToCreate = Array.from(selectedStandards).map(index => {
        const standard = generatedStandards[index];
        let standardState = standard.state ? (getStateNameFromCode(standard.state) || standard.state) : stateForCreate;
        if (standardState && standardState.toUpperCase().trim() === 'COMMON CORE') {
          standardState = 'COMMONCORE';
        } else if (standardState !== 'COMMONCORE') {
          standardState = standardState.toUpperCase().trim();
        }
        
        return {
          abbr: standard.abbr,
          abbr_spanish: standard.abbr_spanish || '',
          description: standard.description,
          description_english: standard.description_english || standard.description,
          state: standardState,
          grade_level: gradeLevel
        };
      });
      const result = await dispatch(handleBulkCreateStandards(standardsToCreate));
      if (result) {
        message.success(`Successfully created ${selectedStandards.size} standard(s)`);
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        message.error('Failed to create standards. Please try again.');
      }
    } catch (error) {
      const errorMessage = error?.message || 'Failed to create standards. Please try again.';
      message.error(errorMessage);
      console.error('Create error:', error);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfProgressIntervalRef.current) {
        clearInterval(pdfProgressIntervalRef.current);
        pdfProgressIntervalRef.current = null;
      }
      if (generationProgressIntervalRef.current) {
        clearInterval(generationProgressIntervalRef.current);
        generationProgressIntervalRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    setStep(1);
    setPdfFile(null);
    setPrompt('');
    setState('');
    setGradeLevel('');
    setGeneratedStandards([]);
    setSelectedStandards(new Set());
    setStreamingComplete(false);
    setStreamingCount(0);
    setPdfUploadProgress(0);
    setGenerationProgress(0);
    setPdfUploaded(false);
    pdfUploadCompleteRef.current = false;
    generationStartedRef.current = false;
    firstStandardReceivedRef.current = false;
    if (pdfProgressIntervalRef.current) {
      clearInterval(pdfProgressIntervalRef.current);
      pdfProgressIntervalRef.current = null;
    }
    if (generationProgressIntervalRef.current) {
      clearInterval(generationProgressIntervalRef.current);
      generationProgressIntervalRef.current = null;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={step === 1 ? 600 : 800}
      title={step === 1 ? 'Add Standard with AI' : 'AI Generated Standards'}
    >
      {step === 1 ? (
        <ModalContentWrapper>
          <FormField>
            <Label>PDF Upload *</Label>
            <Upload
              accept=".pdf"
              beforeUpload={handleFileChange}
              fileList={pdfFile ? [{
                uid: '-1',
                name: pdfFile.name,
                status: 'done',
                originFileObj: pdfFile,
              }] : []}
              onRemove={() => {
                setPdfFile(null);
                return true;
              }}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select PDF</Button>
            </Upload>
            {generating && (
              <ProgressBarContainer>
                <ProgressLabel>{pdfUploaded ? 'PDF uploaded' : 'Uploading PDF...'}</ProgressLabel>
                <LinearProgress
                  variant="determinate"
                  value={pdfUploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#f0f0f0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: pdfUploaded ? '#52c41a' : '#1890ff',
                    },
                  }}
                />
                <ProgressLabel style={{ textAlign: 'right', marginTop: 4 }}>
                  {Math.round(pdfUploadProgress)}%
                </ProgressLabel>
              </ProgressBarContainer>
            )}
          </FormField>

          <FormField>
            <Label>Prompt (Optional)</Label>
            <TextField
              multiline
              rows={4}
              placeholder="Enter instructions for AI parsing..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              variant="outlined"
              fullWidth
            />
          </FormField>

          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="state_label"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  State *
                </InputLabel>
                <Select
                  id="state"
                  name="state"
                  label="State *"
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    setGradeLevel('');
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {STANDARD_GRADE_LEVEL.map((stateConfig) => {
                    const stateName = getStateNameFromCode(stateConfig.code) || stateConfig.state;
                    return (
                      <MenuItem key={stateConfig.code} value={stateName}>
                        {stateName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Col>
            <Col xs={24} sm={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="grade_level_label"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  Grade Level *
                </InputLabel>
                <Select
                  id="grade_level"
                  name="grade_level"
                  label="Grade Level *"
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  disabled={!state}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {state && getGradeLevelsForState(state).map((grade) => (
                    <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Col>
          </Row>

          <div style={{ marginTop: '20px' }}>
            {generating && (
              <ProgressBarWrapper>
                <ProgressLabel>Generating Standards...</ProgressLabel>
                <LinearProgress
                  variant="determinate"
                  value={generationProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#f0f0f0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: '#52c41a',
                    },
                  }}
                />
                <ProgressLabel style={{ textAlign: 'right', marginTop: 4 }}>
                  {Math.round(generationProgress)}%
                </ProgressLabel>
              </ProgressBarWrapper>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: generating ? '16px' : '0' }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleGenerate}
                disabled={!pdfFile || !state || !gradeLevel || generating}
                icon={generating ? <Loading white size={14} /> : null}
              >
                {generating ? (streamingCount > 0 ? `Generating... (${streamingCount} standards)` : 'Generating...') : 'Generate with AI'}
              </Button>
            </div>
          </div>
        </ModalContentWrapper>
      ) : (
        <ModalContentWrapper>
          <ResultsList>
            {generatedStandards.map((standard, index) => {
              const isSelected = selectedStandards.has(index);
              const isDisabled = standard.already_exists === true;
              
              return (
                <StandardItem key={index}>
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                  />
                  <StandardInfo>
                    <StandardAbbr>{standard.abbr || 'N/A'}</StandardAbbr>
                    {standard.abbr_spanish && (
                      <StandardDesc style={{ fontStyle: 'italic', color: '#1890ff' }}>
                        Spanish: {standard.abbr_spanish}
                      </StandardDesc>
                    )}
                    <StandardDesc>{standard.description || 'No description'}</StandardDesc>
                    {standard.description_english && (
                      <StandardDesc>{standard.description_english}</StandardDesc>
                    )}
                    {isDisabled && (
                      <StandardDesc style={{ color: '#ff4d4f', fontStyle: 'italic' }}>
                        Already exists
                      </StandardDesc>
                    )}
                  </StandardInfo>
                </StandardItem>
              );
            })}
            {generating && !streamingComplete && (
              <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                Generating... ({streamingCount} standards)
              </div>
            )}
          </ResultsList>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
            <div>
              Selected: {selectedStandards.size} of {generatedStandards.length}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button
                type="primary"
                onClick={handleCreateSelected}
                disabled={selectedStandards.size === 0 || creating || !streamingComplete}
                icon={creating ? <Loading white size={14} /> : null}
              >
                {creating ? 'Creating...' : 'Create Selected Standards'}
              </Button>
            </div>
          </div>
        </ModalContentWrapper>
      )}
    </Modal>
  );
};

export default AiGenerateStandardsModal;
